import React, { Fragment, useMemo, useState } from 'react';
import { Button, Row, Col, message } from 'antd';
import { API_STATUS, DATE_FORMAT } from 'constants/app-constants';
import dayjs from 'dayjs';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { setMonthlyProductPayments } from 'redux/reducers/payments/paymentAction';
import DatePicker from 'components/date-picker';
import { useI18n } from 'hooks/useI18n';
import { EditableTable } from './EditableTable';

const ProductExpenseExcel = () => {
	const { t } = useI18n();
	const [month, setMonth] = useState(dayjs());
	const globalRedux = useSelector((state) => state.globalRedux);
	const tableLoading = useMemo(() => globalRedux.apiStatus.GET_MONTHLY_PRODUCT_PAYMENTS === API_STATUS.PENDING, [globalRedux.apiStatus]);
	const branchId = globalRedux?.selectedBranch?.id;
	const monthlyProductPayments = useSelector((state) => state.paymentRedux.monthlyProductPayments || []);
	const paddedData = tableLoading ? [] : [...monthlyProductPayments];
	const dispatch = useDispatch();

	const handleMonthChange = async () => {
		dispatch(setMonthlyProductPayments([]));
		let url = `${SERVER_IP}api/direct-payments?branchId=${branchId}&month=${month}`;
		dispatch(getApi('GET_MONTHLY_PRODUCT_PAYMENTS', url));
	};

	// const initialData = [
	// 	{
	// 		id: '464d7f5b-e96e-4619-998a-90a9000b5ecf',
	// 		qty: 4,
	// 		amount: 1000,
	// 		paid: 700,
	// 		balance: 300,
	// 		paidOn: '2025-03-21',
	// 		customer: { customerName: 'Harisj' },
	// 		product: { productName: 'Shake Bottle' },
	// 	},
	// ];

	// Pad the initial data to ensure at least 15 rows
	// setReceivedBy(paddedData[0]?.receivedBy);
	// while (paddedData.length < 1) {
	// 	paddedData.push(createEmptyRow());
	// }

	// Helper function for deep equality check.
	function deepEqual(obj1, obj2) {
		return JSON.stringify(obj1) === JSON.stringify(obj2);
	}

	/**
	 * Compares the original data with the modified data and returns an array of changes.
	 *
	 * @param {Array} originalData - The original array of objects.
	 * @param {Array} updatedData - The modified array of objects.
	 * @returns {Array} Array of change objects with a "modificationType" field (add, update, delete).
	 */
	function getChanges(originalData, updatedData) {
		const changes = [];

		// Create maps keyed by the unique id for fast lookup.
		const originalMap = originalData.reduce((acc, item) => {
			acc[item.id] = item;
			return acc;
		}, {});

		const updatedMap = updatedData.reduce((acc, item) => {
			acc[item.id] = item;
			return acc;
		}, {});

		// Identify deletions: items in the original array not found in the updated array.
		for (const id in originalMap) {
			if (!updatedMap[id]) {
				changes.push({
					modificationType: 'delete',
					data: originalMap[id],
				});
			}
		}

		// Identify additions and updates.
		for (const id in updatedMap) {
			if (!originalMap[id]) {
				// Not present in original, so this is an addition.
				changes.push({
					modificationType: 'add',
					data: updatedMap[id],
				});
			} else {
				// Exists in both; check if there are modifications.
				if (!deepEqual(originalMap[id], updatedMap[id])) {
					changes.push({
						modificationType: 'update',
						data: updatedMap[id],
					});
				}
			}
		}

		return changes;
	}

	// Updated handleSave function that computes and logs the differences.
	const handleSave = (updatedData) => {
		// Compare original data (monthlyProductPayments) with the updated version
		const changes = getChanges(monthlyProductPayments, updatedData);
		if (changes.length === 0) {
			message.error(t('product_expense.messages.no_changes_to_save'));
			return;
		}
		const url = `${SERVER_IP}api/direct-payments/batchUpdate?branchId=` + branchId;
		dispatch(postApi({ changes, month }, 'BATCH_UPDATE_PRODUCT_PAYMENT', url));
		handleMonthChange();
	};

	return (
		<Fragment>
			<Row style={{ padding: 20, paddingBottom: 5 }} gutter={[10, 10]}>
				<Col span={24}>
					<h3>{t('product_expense.title')}</h3>
				</Col>
				<Col span={3}>
					<DatePicker
						style={{ width: '100%' }}
						value={month}
						onChange={setMonth}
						placeholder={t('payments.select_month')}
						className="col-md-3"
						picker="month"
						format={DATE_FORMAT.MM_YYYY}
						allowClear={false}
					/>
				</Col>
				<Col span={3}>
					<Button type="primary" style={{ width: '100%' }} onClick={handleMonthChange}>
						{t('product_expense.actions.load')}
					</Button>
				</Col>
			</Row>
			<Row>
				<Col span={24} style={{ padding: 20 }}>
					<EditableTable dataSource={paddedData} onSave={handleSave} loading={tableLoading} />
				</Col>
			</Row>
		</Fragment>
	);
};

export default ProductExpenseExcel;
